/**
 * Client insights computation.
 *
 * This file derives "insights" about a client from their transactions — popular
 * services, retail purchase patterns, and no-show / cancellation risk flags.
 *
 * NOTE: This logic is expected to change frequently (and may be replaced entirely
 * by a backend-driven analytics endpoint in the future). Keep it isolated in this
 * util so that the UI components that consume it don't need to be rewritten when
 * the underlying heuristics evolve.
 */

import { GetTransactionsResponse } from "../types/transactions";
import { isQuickSaleTransaction } from "../types/quickSale";
import { getLineDiscountDollars } from "./discountUtils";

const NO_SHOW_RISK_THRESHOLD = 0.3;
const CANCEL_RISK_THRESHOLD = 0.3;
const FREQUENT_PRODUCT_THRESHOLD = 0.5;

export interface TopBookedServiceInsight {
  serviceName: string;
  staffName: string;
  count: number;
}

export interface ProductInsight {
  name: string;
  totalQuantity: number;
}

export interface ClientInsights {
  topBookedServices: TopBookedServiceInsight[];
  retailUnitsPurchased: number;
  retailTotalValue: number;
  lastProductBought: ProductInsight | null;
  frequentProduct: ProductInsight | null;
  isHighRiskNoShow: boolean;
  isHighRiskCancellation: boolean;
  noShowRate: number;
  cancellationRate: number;
}

/** A product line at unit price × quantity, net of its discounts. */
function computeProductLineTotal(
  product: Parameters<typeof getLineDiscountDollars>[0],
  quantity: number,
): number {
  const subtotal = (product.unit_price || 0) * quantity;
  return subtotal - getLineDiscountDollars(product, subtotal);
}

/**
 * Derives client insights from their transactions.
 * See interface {@link ClientInsights} for the shape of the result.
 */
export function computeClientInsights(
  transactions: GetTransactionsResponse[] | undefined,
): ClientInsights {
  const txs = transactions ?? [];

  // Service frequency keyed by "service name||staff name"
  const serviceCounts = new Map<
    string,
    { serviceName: string; staffName: string; count: number }
  >();

  // Product frequency keyed by product name
  const productCounts = new Map<string, number>();

  let retailUnitsPurchased = 0;
  let retailTotalValue = 0;

  let lastProductBought: ProductInsight | null = null;
  let lastProductDate: number | null = null;

  // Totals for risk calculations — only transactions with booked_services count.
  let totalWithBookedServices = 0;
  let noShowCount = 0;
  let cancelCount = 0;

  for (const tx of txs) {
    const bookedServices = tx.line_items.booked_services || [];
    const products = tx.line_items.products || [];
    const isQuickSale = isQuickSaleTransaction(tx);
    const status = tx.transaction.appointment_status;

    // Top booked services (only appointment transactions have services)
    for (const bs of bookedServices) {
      const serviceName = bs.name || "Unknown Service";
      const staffName = bs.staff_name || "Unknown Staff";
      const key = `${serviceName}||${staffName}`;
      const existing = serviceCounts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        serviceCounts.set(key, { serviceName, staffName, count: 1 });
      }
    }

    // Retail product aggregation (include products from both appointments and quick sales)
    for (const p of products) {
      const quantity = p.quantity || 0;
      retailUnitsPurchased += quantity;
      retailTotalValue += computeProductLineTotal(p, quantity);

      const productName = p.name || "Unknown Product";
      productCounts.set(
        productName,
        (productCounts.get(productName) || 0) + quantity,
      );

      // Track "last product bought" by transaction created_dt
      const txDate = tx.transaction.created_dt
        ? new Date(tx.transaction.created_dt).getTime()
        : NaN;
      if (
        !Number.isNaN(txDate) &&
        (lastProductDate === null || txDate > lastProductDate)
      ) {
        lastProductDate = txDate;
        lastProductBought = { name: productName, totalQuantity: quantity };
      }
    }

    // Risk rate denominator: transactions with booked_services. Quick sales are
    // excluded because they aren't appointments and can't be "no-show".
    if (!isQuickSale && bookedServices.length > 0) {
      totalWithBookedServices += 1;
      if (status === "No Show") noShowCount += 1;
      if (status === "Cancelled") cancelCount += 1;
    }
  }

  // Top 3 services by count (ties broken by service name for stability)
  const topBookedServices = Array.from(serviceCounts.values())
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.serviceName.localeCompare(b.serviceName);
    })
    .slice(0, 3);

  // Frequent product: purchased in more than 50% of transactions that include products.
  // We count distinct product name occurrences across transactions (any quantity counts).
  const txsWithProducts = txs.filter(
    (tx) => (tx.line_items.products || []).length > 0,
  );
  let frequentProduct: ProductInsight | null = null;
  if (txsWithProducts.length > 0) {
    const txProductCounts = new Map<string, number>();
    for (const tx of txsWithProducts) {
      const seenInTx = new Set<string>();
      for (const p of tx.line_items.products || []) {
        const name = p.name || "Unknown Product";
        if (!seenInTx.has(name)) {
          seenInTx.add(name);
          txProductCounts.set(name, (txProductCounts.get(name) || 0) + 1);
        }
      }
    }
    let bestName: string | null = null;
    let bestCount = 0;
    Array.from(txProductCounts.entries()).forEach(([name, count]) => {
      if (count > bestCount) {
        bestName = name;
        bestCount = count;
      }
    });
    if (
      bestName &&
      bestCount / txsWithProducts.length > FREQUENT_PRODUCT_THRESHOLD
    ) {
      frequentProduct = {
        name: bestName,
        totalQuantity: productCounts.get(bestName) || 0,
      };
    }
  }

  const noShowRate =
    totalWithBookedServices > 0 ? noShowCount / totalWithBookedServices : 0;
  const cancellationRate =
    totalWithBookedServices > 0 ? cancelCount / totalWithBookedServices : 0;

  return {
    topBookedServices,
    retailUnitsPurchased,
    retailTotalValue,
    lastProductBought,
    frequentProduct,
    isHighRiskNoShow: noShowRate > NO_SHOW_RISK_THRESHOLD,
    isHighRiskCancellation: cancellationRate > CANCEL_RISK_THRESHOLD,
    noShowRate,
    cancellationRate,
  };
}
